import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import TopNav from '../components/TopNav';
import SocialFooter from '../components/SocialFooter';
import { Check, Star, SpinnerGap } from '@phosphor-icons/react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cardio-journey-app/artifacts/tyj7q2ct_logo%20365.png";
const MAN_WRITING = "https://customer-assets.emergentagent.com/job_3088e77d-725d-4c0a-bf3e-16c63c18c94f/artifacts/0rvyxzcp_6EDDCCE0-D469-44DC-AA67-AE7D10985C26.jpeg";

const PLANS = [
  {
    id: 'starter',
    name: 'Starter',
    price: 0,
    period: '',
    description: 'Basic cardio tracking to get started',
    features: ['Log workouts', 'View last 7 days', 'Basic stats', 'Weight tracking'],
    cta: 'Get Started Free',
    popular: false,
  },
  {
    id: 'monthly',
    name: 'Monthly',
    price: 7.99,
    period: '/mo',
    description: 'Full tracking + analytics',
    features: ['Full 365-day tracking', 'All 8 machine trackers', 'Progress analytics', 'Streak tracking', 'Weekly reports'],
    cta: 'Subscribe Now',
    popular: false,
  },
  {
    id: 'annual',
    name: 'Annual',
    price: 49.99,
    period: '/yr',
    yearlyNote: 'Just $4.17/mo',
    savings: 'Save $45.89 vs monthly',
    description: 'Best value - full access all year',
    features: ['Everything in Monthly', 'Before & After photos', 'Advanced insights', 'Priority support', 'Full year access'],
    cta: 'Subscribe Now',
    popular: true,
  },
];

const Pricing = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [loadingPlan, setLoadingPlan] = useState(null);

  const handleSelectPlan = async (planId) => {
    if (planId === 'starter') {
      navigate('/register');
      return;
    }

    if (!user) {
      navigate('/register', { state: { plan: planId } });
      return;
    }

    setLoadingPlan(planId);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const originUrl = window.location.origin;
      const res = await axios.post(`${API}/checkout/create-session`, {
        plan_id: planId,
        origin_url: originUrl,
      }, { withCredentials: true, headers });

      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setLoadingPlan(null);
    }
  };

  return (
    <div className="min-h-screen bg-background relative overflow-hidden" data-testid="pricing-screen">
      <div className="absolute inset-0 opacity-[0.08] pointer-events-none" style={{ backgroundImage: `url(${LOGO_URL})`, backgroundPosition: 'center', backgroundRepeat: 'no-repeat', backgroundSize: '50%' }} />

      {/* Logo bar without Buy the Book */}
      <div className="relative z-50 flex items-center px-6 py-3 border-b border-border bg-background/80 backdrop-blur-xl">
        <img src={LOGO_URL} alt="Track365" className="w-40 h-auto" data-testid="pricing-logo" />
      </div>

      <TopNav showHome={true} />

      <main className="max-w-4xl mx-auto px-6 py-6 relative z-10">
        {/* Header with image on left - 50% smaller */}
        <div className="flex flex-col sm:flex-row items-center gap-6 mb-6">
          <div className="rounded-2xl overflow-hidden shadow-lg border border-[#D4AF37]/10 w-[100px] sm:w-[110px] flex-shrink-0">
            <img src={MAN_WRITING} alt="Man tracking cardio" className="w-full h-auto object-contain" />
          </div>
          <div className="text-center sm:text-left">
            <h1 className="text-3xl sm:text-4xl font-black text-foreground mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Choose Your Plan
            </h1>
            <p className="text-muted-foreground mb-2">Start tracking. Start seeing results.</p>
            <Link to="/refund-policy" className="text-xs text-[#D4AF37] hover:underline" data-testid="refund-link-pricing">
              View Refund & Billing Policy
            </Link>
          </div>
        </div>

        {/* 3 Pricing Cards */}
        <div className="grid sm:grid-cols-3 gap-4 mb-6">
          {PLANS.map((plan) => (
            <div key={plan.id} className="flex flex-col">
              {plan.popular && (
                <div className="flex justify-center mb-2">
                  <span className="bg-[#D4AF37] text-black text-[10px] font-bold px-3 py-1 rounded-full flex items-center gap-1" data-testid="best-value-badge">
                    <Star weight="fill" className="w-2.5 h-2.5" />
                    BEST VALUE
                  </span>
                </div>
              )}
              {!plan.popular && <div className="mb-2 h-[22px]" />}

              <div className={`premium-card p-5 flex-1 flex flex-col ${plan.popular ? 'border-[#D4AF37]/50 ring-1 ring-[#D4AF37]/20' : ''}`}>
                <div className="text-center mb-4">
                  <h3 className="text-base font-bold text-foreground mb-1">{plan.name}</h3>
                  <p className="text-[10px] text-muted-foreground mb-3">{plan.description}</p>
                  <div className="flex items-baseline justify-center gap-1">
                    {plan.price === 0 ? (
                      <span className="text-2xl font-bold text-foreground">Free</span>
                    ) : (
                      <>
                        <span className="text-2xl font-bold text-foreground">${plan.price}</span>
                        <span className="text-xs text-muted-foreground">{plan.period}</span>
                      </>
                    )}
                  </div>
                  {plan.savings && (
                    <div className="mt-2 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-lg px-3 py-1.5" data-testid="savings-box">
                      <p className="text-[10px] text-[#D4AF37] font-bold">{plan.yearlyNote}</p>
                      <p className="text-[9px] text-[#D4AF37]/70">{plan.savings}</p>
                    </div>
                  )}
                </div>
                <ul className="space-y-1.5 mb-4 flex-1">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-xs">
                      <Check weight="bold" className="w-3.5 h-3.5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => handleSelectPlan(plan.id)}
                  disabled={loadingPlan === plan.id}
                  className={`w-full rounded-full py-4 text-sm font-semibold ${plan.popular ? 'bg-[#D4AF37] text-black hover:bg-[#F3E5AB]' : 'bg-card border border-border text-foreground hover:bg-[#D4AF37]/10'}`}
                  data-testid={`select-${plan.id}-btn`}
                >
                  {loadingPlan === plan.id ? (
                    <SpinnerGap weight="bold" className="w-4 h-4 animate-spin mr-2" />
                  ) : null}
                  {loadingPlan === plan.id ? 'Redirecting...' : plan.cta}
                </Button>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mb-6">
          <p className="text-xs text-muted-foreground">
            Secure payment powered by Stripe. Apple Pay, Google Pay & Link accepted.
          </p>
        </div>

        <SocialFooter />
      </main>
    </div>
  );
};

export default Pricing;
